import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useMemo } from 'react'
import { GetProductWarehouseItemsStockLevelTotalEnum } from '../generated/graphql'
import { SelectInput } from '../inputs/select-input'
import { useFormFilter } from '../hooks/form-filter'
import { FilterContext } from '../components/data-grid/context'

export type StockLevelFilter = {
  stockLevelTotal?: GetProductWarehouseItemsStockLevelTotalEnum
}

export const StockLevelTotalFilter = () => {
  const { t } = useTranslation('filter')

  const filterKey = 'stockLevelTotal'

  const { setFilter, subscribeOnResetFilter, removeFilter, registerFilter } =
    useContext<FilterContext<StockLevelFilter>>(FilterContext)

  const options = useMemo(
    () => [
      {
        name: t('filter:stock_level_total.All'),
        value: '',
      },
      ...Object.values(GetProductWarehouseItemsStockLevelTotalEnum).map(
        (value) => ({
          name: t(`filter:stock_level_total.${value}`),
          value: value,
        }),
      ),
    ],
    [t],
  )

  const { queryParams, formContext, resetForm } =
    useFormFilter<StockLevelFilter>({
      formProps: {
        defaultValues: {
          stockLevelTotal: undefined,
        },
      },
    })

  const { stockLevelTotal } = queryParams

  useEffect(() => {
    registerFilter({ key: filterKey })
  }, [registerFilter])

  useEffect(() => {
    const label = options.find(({ value }) => value === stockLevelTotal)?.name

    if (stockLevelTotal && label) {
      setFilter(filterKey, {
        label: t('filter:stock_level_total.label'),
        labelValues: [label],
        value: stockLevelTotal,
        isDefault: false,
      })
    } else {
      removeFilter(filterKey)
    }
  }, [options, stockLevelTotal, setFilter, removeFilter, t])

  useEffect(() => {
    const unsubscribe = subscribeOnResetFilter((key) => {
      if (!key || key === filterKey) {
        resetForm()
      }
    })

    return () => unsubscribe()
  }, [resetForm, subscribeOnResetFilter])

  return (
    <SelectInput
      name="stockLevelTotal"
      control={formContext.control}
      options={options}
      label={t('filter:stock_level_total.label')}
      slotProps={{ select: { displayEmpty: true } }}
      transform={{
        input: (value) => (value ? value : ''),
      }}
    />
  )
}
